import { ConsultaSeguimentoIndicatorResultadoText } from '@mchat-rf/consulta-seguimento/components/ConsultaSeguimentoIndicatorResultado'
import { PassouFalhouEnum } from '@mchat-rf/consulta-seguimento/enums-consultaSeguimento'
import {
  hasAnySim,
  isAllFilled,
  isAllNao,
  isPerguntaOutroFilled,
  isPerguntaOutroSelected,
  isPerguntaOutroSim,
} from '@mchat-rf/consulta-seguimento/utils-consultaSeguimento'
import { SimNaoEnum } from 'components/form'
import { isEmpty } from 'util/checks'

import {
  ConsultaSeguimentoPergunta12ExemplosEnum,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum,
  ConsultaSeguimentoPergunta12Model,
} from './model-consultaSeguimentoPergunta12'

const pergunta12ExemplosEnumValues = Object.values(ConsultaSeguimentoPergunta12ExemplosEnum)

const pergunta12ExemplosPassouEnumValues = [
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS_CALMAMENTE,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.NAO_GOSTA_BARULHO,
]
const pergunta12ExemplosFalhouEnumValues = [
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.GRITA,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.CHORA,
  ConsultaSeguimentoPergunta12ExemplosPassouFalhouEnum.TAMPA_OUVIDOS,
]

export function isPergunta12ExemplosAllFilled(value: ConsultaSeguimentoPergunta12Model): boolean {
  const isExemplosAllFilled = isAllFilled(value?.PERGUNTA_12_EXEMPLOS, pergunta12ExemplosEnumValues)

  if (isPerguntaOutroFilled(value?.OUTRO)) {
    return isExemplosAllFilled && isPerguntaOutroSelected(value?.OUTRO)
  }

  return isExemplosAllFilled
}

export function hasPergunta12ExemplosAtLeastTwoSim(value: ConsultaSeguimentoPergunta12Model): boolean {
  const exemplosMarkedAsSim = Object.values(value?.PERGUNTA_12_EXEMPLOS ?? {}).filter((item) => item === SimNaoEnum.SIM)

  if (isPerguntaOutroSim(value?.OUTRO)) {
    return exemplosMarkedAsSim.length >= 1
  }

  return exemplosMarkedAsSim.length >= 2
}

export function isPergunta12ExemplosPassouFalhouAllFilled(
  value: ConsultaSeguimentoPergunta12Model['PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU']
): boolean {
  return (
    isAllFilled(value?.PASSOU, pergunta12ExemplosPassouEnumValues) &&
    isAllFilled(value?.FALHOU, pergunta12ExemplosFalhouEnumValues)
  )
}

export function isPergunta12ExemplosPassouFalhouAllNao(
  value: ConsultaSeguimentoPergunta12Model['PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU']
): boolean {
  return (
    isAllNao(value?.PASSOU, pergunta12ExemplosPassouEnumValues) &&
    isAllNao(value?.FALHOU, pergunta12ExemplosFalhouEnumValues)
  )
}

export function hasPergunta12ExemplosPassouAnySim(
  value: ConsultaSeguimentoPergunta12Model['PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU']
): boolean {
  return hasAnySim(value?.PASSOU)
}

export function hasPergunta12ExemplosFalhouAnySim(
  value: ConsultaSeguimentoPergunta12Model['PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU']
): boolean {
  return hasAnySim(value?.FALHOU)
}

export function getPergunta12ResultadoText(
  value: ConsultaSeguimentoPergunta12Model
): ConsultaSeguimentoIndicatorResultadoText {
  let textResultadoPassou: string | undefined
  let textResultadoFalhou: string | undefined

  const isExemplosAllFilled = isPergunta12ExemplosAllFilled(value)
  const hasExemplosAtLeastTwoSim = hasPergunta12ExemplosAtLeastTwoSim(value)

  if (value.PERGUNTA_12_HEADER === SimNaoEnum.SIM && isExemplosAllFilled && !hasExemplosAtLeastTwoSim) {
    textResultadoPassou = 'Sim para um ou nenhum:'
  }

  if (value.PERGUNTA_12_HEADER === SimNaoEnum.SIM && hasExemplosAtLeastTwoSim) {
    const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
    const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(value?.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
    const hasSimOnlyInExemplosPassou = hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim
    const hasSimOnlyInExemplosFalhou = hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim

    textResultadoPassou = hasSimOnlyInExemplosPassou
      ? 'Sim somente para exemplos Passou:'
      : 'Os mais frequentes são exemplos de Passou:'

    textResultadoFalhou = hasSimOnlyInExemplosFalhou
      ? 'Sim somente para exemplos Falhou:'
      : 'Os mais frequentes são exemplos de Falhou:'
  }

  return {
    PASSOU: textResultadoPassou,
    FALHOU: textResultadoFalhou,
  }
}

export function prunePergunta12(value: ConsultaSeguimentoPergunta12Model): ConsultaSeguimentoPergunta12Model {
  const pruned: ConsultaSeguimentoPergunta12Model = {
    status: value.status,
    resultado: value.resultado,
    PERGUNTA_12_HEADER: value.PERGUNTA_12_HEADER,
  }

  const isHeaderSim = value.PERGUNTA_12_HEADER === SimNaoEnum.SIM
  const hasAtLeastTwoSim = hasPergunta12ExemplosAtLeastTwoSim(value)
  const hasPassouAnySim = hasPergunta12ExemplosPassouAnySim(value.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(value.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)

  if (isHeaderSim) {
    pruned.PERGUNTA_12_EXEMPLOS = value.PERGUNTA_12_EXEMPLOS
    pruned.OUTRO = value.OUTRO
  }

  if (isHeaderSim && hasAtLeastTwoSim) {
    pruned.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU = value.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  }

  if (isHeaderSim && hasAtLeastTwoSim && hasPassouAnySim && hasFalhouAnySim) {
    pruned.PERGUNTA_12_FREQUENCIA = value.PERGUNTA_12_FREQUENCIA
  }

  return pruned
}

export function conditionToCalculatePergunta12(values: ConsultaSeguimentoPergunta12Model): boolean {
  if (values.PERGUNTA_12_HEADER === SimNaoEnum.NAO) {
    return true
  }

  const isExemplosAllFilled = isPergunta12ExemplosAllFilled(values)
  const hasExemplosAtLeastTwoSim = hasPergunta12ExemplosAtLeastTwoSim(values)

  if (isExemplosAllFilled && !hasExemplosAtLeastTwoSim) {
    return true
  }

  if (!hasExemplosAtLeastTwoSim) {
    return false
  }

  if (!isPergunta12ExemplosPassouFalhouAllFilled(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)) {
    return false
  }

  const isPerguntaExemplosPassouFalhouAllNao = isPergunta12ExemplosPassouFalhouAllNao(
    values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU
  )

  if (isPerguntaExemplosPassouFalhouAllNao) {
    return false
  }

  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)

  if (
    (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) ||
    (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim)
  ) {
    return true
  } else {
    return !isEmpty(values.PERGUNTA_12_FREQUENCIA)
  }
}

export function calculateResultadoPergunta12(values: ConsultaSeguimentoPergunta12Model): PassouFalhouEnum | undefined {
  if (values.PERGUNTA_12_HEADER === SimNaoEnum.NAO || !hasPergunta12ExemplosAtLeastTwoSim(values)) {
    return PassouFalhouEnum.PASSOU
  }

  const hasPerguntaExemplosPassouAnySim = hasPergunta12ExemplosPassouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)
  const hasPerguntaExemplosFalhouAnySim = hasPergunta12ExemplosFalhouAnySim(values.PERGUNTA_12_EXEMPLOS_PASSOU_FALHOU)

  if (hasPerguntaExemplosPassouAnySim && !hasPerguntaExemplosFalhouAnySim) {
    return PassouFalhouEnum.PASSOU
  } else if (hasPerguntaExemplosFalhouAnySim && !hasPerguntaExemplosPassouAnySim) {
    return PassouFalhouEnum.FALHOU
  } else {
    return values.PERGUNTA_12_FREQUENCIA
  }
}
